import { useMemo, useState } from 'react'
import { TrendingUp, Calendar, Info, Flag, Mail, MessageCircle, Megaphone, Play, CheckCircle, AlertCircle, Star, type LucideIcon } from 'lucide-react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceLine,
  LabelList,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/shared/components/ui/chart'

interface DataPoint {
  fecha: string
  total: number
  acumulado?: number
}

interface MarcadorEspecial {
  fecha: string
  titulo: string
  descripcion?: string
  color: string
  tipo: string
}

interface RegistrosPorFechaChartProps {
  data: DataPoint[]
  marcadores?: MarcadorEspecial[]
  lineaTendencia?: DataPoint[]
  // 'dia' → serie día a día; 'mes' → serie mes a mes (agregada en el backend)
  agrupacion?: 'dia' | 'mes'
  isLoading?: boolean
}

interface TooltipState {
  titulo: string
  color: string
  clientX: number
  clientY: number
}

// Parsear fecha sin problemas de timezone
const parseDateSafe = (dateString: string): Date => {
  if (dateString.includes('T')) {
    return new Date(dateString)
  }
  return new Date(dateString + 'T12:00:00')
}

const chartConfig = {
  total: {
    label: 'Registros',
    color: '#0d9488',
  },
} satisfies ChartConfig

const getIconForType = (tipo: string): LucideIcon => {
  const t = tipo.toLowerCase()
  if (t.includes('mail') || t.includes('email') || t.includes('correo')) return Mail
  if (t.includes('whatsapp') || t.includes('mensaje') || t.includes('sms')) return MessageCircle
  if (t.includes('campana') || t.includes('campaña') || t.includes('campaign')) return Megaphone
  if (t.includes('inicio') || t.includes('start') || t.includes('evento')) return Play
  if (t.includes('fin') || t.includes('end') || t.includes('completado')) return CheckCircle
  if (t.includes('alerta') || t.includes('warning') || t.includes('importante')) return AlertCircle
  if (t.includes('hito') || t.includes('milestone') || t.includes('destacado')) return Star
  return Flag
}

// Componente que se inyecta como label del ReferenceLine (renderiza dentro del SVG)
interface HitoMarkerProps {
  viewBox?: { x: number; y: number; width: number; height: number }
  marcador: MarcadorEspecial
  onEnter: (e: React.MouseEvent<SVGGElement>, m: MarcadorEspecial) => void
  onLeave: () => void
}

function HitoMarker({ viewBox, marcador, onEnter, onLeave }: HitoMarkerProps) {
  const x = viewBox?.x ?? 0
  const y = viewBox?.y ?? 0
  const IconComponent = getIconForType(marcador.tipo)
  const r = 10
  const iconSize = 12
  // Centrar el ícono en la mitad del margen superior (margin.top=50 → punto medio y=25)
  const cy = y - 25

  return (
    <g
      onMouseEnter={(e) => onEnter(e, marcador)}
      onMouseLeave={onLeave}
      style={{ cursor: 'default' }}
    >
      {/* Círculo de fondo */}
      <circle
        cx={x}
        cy={cy}
        r={r}
        fill="white"
        stroke={marcador.color}
        strokeWidth={1.5}
      />
      {/* Ícono via foreignObject */}
      <foreignObject
        x={x - iconSize / 2}
        y={cy - iconSize / 2}
        width={iconSize}
        height={iconSize}
        style={{ overflow: 'visible' }}
      >
        <div
          style={{
            width: iconSize,
            height: iconSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: marcador.color,
          }}
        >
          <IconComponent size={iconSize} strokeWidth={2} />
        </div>
      </foreignObject>
    </g>
  )
}

export function RegistrosPorFechaChart({
  data,
  marcadores = [],
  agrupacion = 'dia',
  isLoading = false,
}: RegistrosPorFechaChartProps) {
  // Formatea la etiqueta del eje X según la agrupación (día a día o mes a mes)
  const formatFechaEje = (fecha: string): string =>
    agrupacion === 'mes'
      ? parseDateSafe(fecha).toLocaleDateString('es-EC', { month: 'short', year: 'numeric' })
      : parseDateSafe(fecha).toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })

  const [showInfo, setShowInfo] = useState(false)
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)

  const handleHitoEnter = (e: React.MouseEvent<SVGGElement>, marcador: MarcadorEspecial) => {
    setTooltip({
      titulo: marcador.titulo,
      color: marcador.color,
      clientX: e.clientX,
      clientY: e.clientY,
    })
  }

  const handleHitoLeave = () => setTooltip(null)

  const formattedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      fechaDisplay: formatFechaEje(item.fecha),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, agrupacion])

  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Registros por Fecha
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground text-xs">Cargando...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Registros por Fecha
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[280px] flex flex-col items-center justify-center text-muted-foreground">
            <Calendar className="h-6 w-6 mb-2 text-muted-foreground/50" />
            <p className="text-xs font-medium">No hay datos de fechas</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700 w-full">
      <CardHeader className="pb-0 pt-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          Registros por Fecha
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Info className="h-4 w-4" />
          </button>
          {showInfo && (
            <span className="text-xs font-normal text-muted-foreground animate-in slide-in-from-left-2 duration-200">
              Histórico de registros {agrupacion === 'mes' ? 'mensuales' : 'diarios'}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 px-1">
        <div className="relative">
          <ChartContainer config={chartConfig} className="!aspect-auto h-[280px] w-full max-w-full">
            <AreaChart
              data={formattedData}
              margin={{ top: 50, right: 15, left: 5, bottom: 15 }}
            >
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#0d9488" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#0d9488" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
              <XAxis
                dataKey="fechaDisplay"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                interval="preserveStartEnd"
                className="text-xs"
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={false}
                width={10}
              />
              <ChartTooltip
                cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '4 4' }}
                content={
                  <ChartTooltipContent
                    labelFormatter={(_, payload) => {
                      if (payload?.[0]?.payload?.fecha) {
                        return parseDateSafe(payload[0].payload.fecha).toLocaleDateString(
                          'es-EC',
                          agrupacion === 'mes'
                            ? { month: 'long', year: 'numeric' }
                            : {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                              }
                        )
                      }
                      return ''
                    }}
                  />
                }
              />
              {marcadores.map((marcador, index) => (
                <ReferenceLine
                  key={index}
                  x={formatFechaEje(marcador.fecha)}
                  stroke={marcador.color}
                  strokeDasharray="5 5"
                  label={
                    <HitoMarker
                      marcador={marcador}
                      onEnter={handleHitoEnter}
                      onLeave={handleHitoLeave}
                    />
                  }
                />
              ))}
              <Area
                type="monotone"
                dataKey="total"
                stroke="#0d9488"
                strokeWidth={2}
                fill="url(#colorTotal)"
                dot={(props) => {
                  const { cx, cy, payload } = props
                  if (payload.total > 0) {
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={3}
                        fill="#0d9488"
                        stroke="white"
                        strokeWidth={1.5}
                      />
                    )
                  }
                  return <g />
                }}
                activeDot={{ r: 5, fill: '#0d9488', stroke: 'white', strokeWidth: 2 }}
              >
                <LabelList
                  dataKey="total"
                  position="top"
                  offset={8}
                  style={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
                  formatter={(value: number) => (value > 0 ? value : '')}
                />
              </Area>
            </AreaChart>
          </ChartContainer>

          {/* Tooltip flotante — fixed para escapar cualquier overflow del contenedor */}
          {tooltip && (
            <div
              className="fixed z-[9999] pointer-events-none px-2 py-1 rounded text-xs font-medium shadow-md border bg-background text-foreground whitespace-nowrap"
              style={{
                left: tooltip.clientX + 12,
                top: tooltip.clientY - 32,
                borderColor: tooltip.color,
                borderLeftWidth: 3,
              }}
            >
              {tooltip.titulo}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
